import type { Line, RevisionContent, Section } from '../schema/v1'
import { normalizeForSearch } from '../search'

/**
 * Exporta a ChordPro. ChordPro no guarda la duración de los acordes ni la procedencia, así que
 * la exportación avisa de lo que se pierde (§3.4). El JSON propio es la fuente de verdad.
 */
export interface SerializeInput {
  title?: string
  artist?: string
  content: RevisionContent
  /** Con `false`, solo el cuerpo: sin título, artista, tono, cejilla, tempo ni compás. */
  header?: boolean
}

export interface SerializeResult {
  text: string
  /** Lo que ChordPro no puede guardar, explicado para la persona. */
  losses: string[]
}

const ENVIRONMENT_BY_KIND: Record<Section['kind'], string> = {
  intro: 'intro',
  verse: 'verse',
  prechorus: 'prechorus',
  chorus: 'chorus',
  bridge: 'bridge',
  instrumental: 'instrumental',
  solo: 'solo',
  interlude: 'interlude',
  outro: 'outro',
  tab: 'tab',
  other: 'part',
}

export function serializeLyricLine(line: Pick<Line, 'text' | 'chords'>): string {
  let output = ''
  let cursor = 0
  for (const anchor of line.chords) {
    const at = Math.min(Math.max(anchor.at, cursor), line.text.length)
    output += line.text.slice(cursor, at) + `[${anchor.chord}]`
    cursor = at
  }
  return output + line.text.slice(cursor)
}

function serializeLine(line: Line): string {
  if (line.kind === 'comment') return `{comment: ${line.text}}`
  return serializeLyricLine(line)
}

function serializeSection(section: Section): string[] {
  const body = section.lines.map(serializeLine)
  if (section.kind === 'other' && !section.label) return body
  const environment = ENVIRONMENT_BY_KIND[section.kind]
  const open = section.label
    ? `{start_of_${environment}: ${section.label}}`
    : `{start_of_${environment}}`
  return [open, ...body, `{end_of_${environment}}`]
}

const labelKey = (label: string) => normalizeForSearch(label).replace(/\s+/g, ' ').trim()

export function serializeChordPro({
  title,
  artist,
  content,
  header: includeHeader = true,
}: SerializeInput): SerializeResult {
  const losses: string[] = []
  const header: string[] = []
  if (includeHeader) {
    if (title) header.push(`{title: ${title}}`)
    if (artist) header.push(`{artist: ${artist}}`)
    for (const [name, value] of Object.entries(content.meta ?? {})) {
      header.push(`{${name}: ${value}}`)
    }
    if (content.shapeKey) header.push(`{key: ${content.shapeKey}}`)
    if (content.capo > 0) header.push(`{capo: ${content.capo}}`)
    if (content.tempo.reference) header.push(`{tempo: ${content.tempo.reference.bpm}}`)
    if (content.timeSignature) header.push(`{time: ${content.timeSignature}}`)
  }

  const blocks: string[][] = []
  if (header.length > 0) blocks.push(header)

  // Las secciones se escriben en el orden en que aparecen por primera vez; ese orden da el
  // número de «#n» y es el mismo en el que el parser las vuelve a abrir.
  const byId = new Map(content.sections.map((section) => [section.id, section]))
  const written: Section[] = []
  const labelCount = new Map<string, number>()
  for (const section of content.sections) {
    if (section.label) {
      const key = labelKey(section.label)
      labelCount.set(key, (labelCount.get(key) ?? 0) + 1)
    }
  }
  const firstUse = new Set<string>()
  for (const occurrence of content.sequence) {
    const section = byId.get(occurrence.section)
    if (section && !firstUse.has(section.id)) {
      firstUse.add(section.id)
      written.push(section)
    }
  }
  const unused = content.sections.filter((section) => !firstUse.has(section.id))
  written.push(...unused)

  const emitted = new Set<string>()
  let lastChorus: string | null = null
  for (const occurrence of content.sequence) {
    const section = byId.get(occurrence.section)
    if (!section) continue
    if (!emitted.has(section.id)) {
      emitted.add(section.id)
      blocks.push(serializeSection(section))
      if (section.kind === 'chorus') lastChorus = section.id
    } else if (section.kind === 'chorus' && lastChorus === section.id) {
      // `{chorus}` repite el último estribillo escrito; cualquier otra repetición lleva su referencia.
      blocks.push(['{chorus}'])
    } else {
      const unique = section.label && labelCount.get(labelKey(section.label)) === 1
      const reference = unique ? section.label! : `#${written.indexOf(section) + 1}`
      blocks.push([`{repeat: ${reference}}`])
    }
  }
  for (const section of unused) {
    emitted.add(section.id)
    blocks.push(serializeSection(section))
  }

  if (unused.length > 0) losses.push('Secciones que no están en la secuencia de interpretación')
  if (content.beatMap.status !== 'absent')
    losses.push('El mapa de tiempos (duración de cada acorde)')
  if (content.soundingKey) losses.push('La tonalidad que suena')
  if (content.tuning) losses.push('La afinación')
  if (content.beatUnit) losses.push('La unidad de pulso')
  if (content.notes) losses.push('Las notas')
  const nonImport = Object.values(content.provenance).some((entry) => entry.kind !== 'import')
  if (nonImport) losses.push('La procedencia de cada dato')

  return { text: blocks.map((block) => block.join('\n')).join('\n\n') + '\n', losses }
}
